import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Badge, Row, Col, Container, Spinner } from 'react-bootstrap';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaEdit, FaTrash, FaCalendarAlt, FaUser, FaBoxes } from 'react-icons/fa';

const PIssueSlipView = () => {
    const { id, pointindex } = useParams();
    const navigate = useNavigate();
    const [issueSlipData, setIssueSlipData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchIssueSlipData = async () => {
            try {
                const response = await axios.get(`${BASE_URL}materialissueslip/${id}`, {
                    headers: authHeader()
                });
                setIssueSlipData(response.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching issue slip data", err);
                setError(err.message);
                setLoading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch issue slip data',
                    confirmButtonText: 'OK'
                }).then(() => {
                    navigate(-1);
                });
            }
        };

        fetchIssueSlipData();
    }, [id]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error || !issueSlipData) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger text-center" role="alert">
                    {error || "No material issue slip data found"}
                </div>
                <div className="text-center">
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                        <FaArrowLeft className="me-1" /> Go Back
                    </Button>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Open': return 'success';
            case 'Close': return 'secondary';
            case 'Draft': return 'warning';
            default: return 'info';
        }
    };

    const getStockTypeBadge = (stockType) => {
        switch (stockType) {
            case 'inward': return 'success';
            case 'outward': return 'primary';
            default: return 'info';
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${BASE_URL}materialissueslip/${id}`, {
                    headers: authHeader(),
                });
                navigate(`/issue-slip-list/${pointindex}`);
                Swal.fire('Deleted!', 'Material issue slip deleted successfully.', 'success');
            } catch (error) {
                Swal.fire('Error', error.response?.data || 'An error occurred', 'error');
            }
        }
    };

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">MATERIAL ISSUE SLIP - VIEW</h5>
                        <div>
                            <Link to={`/IssueSlipUpdate/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                                <FaEdit className="me-1" />
                                Update
                            </Link>
                            <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleDelete(id)}>
                                <FaTrash className="me-1" />
                                Delete
                            </Button>
                            <Link to={`/issue-slip-list/${pointindex}`} className="btn btn-secondary btn-sm">
                                <FaArrowLeft className="me-1" />
                                Back
                            </Link>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Production Order No</small>
                                <div className="fw-bold">{issueSlipData.productionordernumber || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Type</small>
                                <div className="fw-bold">{issueSlipData.type || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Stock Type</small>
                                <div>
                                    <Badge bg={getStockTypeBadge(issueSlipData.stocktype)} className="px-2 py-1">
                                        {issueSlipData.stocktype?.toUpperCase() || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge bg={getStatusBadge(issueSlipData.status)} className="px-2 py-1">
                                        {issueSlipData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Issue Slip Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Issue Slip Details</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Production Order No</small>
                            <div className="fw-bold">{issueSlipData.productionordernumber || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Part No</small>
                            <div className="fw-bold">{issueSlipData.partno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Job Number</small>
                            <div className="fw-bold">{issueSlipData.jobnumber || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Issue Slip Date</small>
                            <div className="fw-bold">
                                <FaCalendarAlt className="me-2 text-muted" size={12} />
                                {formatDate(issueSlipData.issueslipdate)}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Issued & Received By */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Issue & Receive Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <small className="text-muted">Issued By</small>
                            <div className="fw-bold">
                                <FaUser className="me-2 text-muted" size={12} />
                                {issueSlipData.issuedby?.map(user => user.firstname).join(', ') || '-'}
                            </div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">Received By</small>
                            <div className="fw-bold">
                                <FaUser className="me-2 text-muted" size={12} />
                                {issueSlipData.receivedby?.map(user => user.firstname).join(', ') || '-'}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Pump Raw/Semi Product Items */}
            {issueSlipData.materialissuewithbomrawsemiservicespump?.length > 0 && (
                <div className="shadow-sm border rounded bg-white mb-3">
                    <div className="bg-light py-2 px-3 border-bottom">
                        <h6 className="mb-0 fw-bold">
                            <FaBoxes className="me-2" size={14} />
                            Pump Raw / Semi Product Items
                        </h6>
                    </div>
                    <div className="p-0">
                        <div className="table-responsive">
                            <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-center fw-bold py-2">#</th>
                                        <th className="fw-bold py-2">Part Name</th>
                                        <th className="fw-bold py-2">Material Size</th>
                                        <th className="fw-bold py-2">MOC</th>
                                        <th className="fw-bold py-2 text-center">Required Qty</th>
                                        <th className="fw-bold py-2 text-center">Issued Qty</th>
                                        <th className="fw-bold py-2 text-center">UOM</th>
                                        <th className="fw-bold py-2">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {issueSlipData.materialissuewithbomrawsemiservicespump.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="text-center">{index + 1}</td>
                                            <td className="fw-semibold">{item.description || '-'}</td>
                                            <td>{item.materialsize || '-'}</td>
                                            <td>{item.moc || '-'}</td>
                                            <td className="text-center">{item.requiredquantity || '0'}</td>
                                            <td className="text-center">{item.issuedquantity || '0'}</td>
                                            <td className="text-center">{item.uom || '-'}</td>
                                            <td>{item.remark || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Link to={`/IssueSlipUpdate/${id}/${pointindex}`} className="btn btn-primary btn-sm me-2">
                    <FaEdit className="me-1" />
                    Update Issue Slip
                </Link>
                <Link to={`/issue-slip-list/${pointindex}`} className="btn btn-secondary btn-sm">
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Link>
            </div>
        </Container>
    );
};

export default PIssueSlipView;